/// <reference lib="dom" />

import { createTRPCClient, httpBatchLink } from '@trpc/client'
import { createTRPCReact, type CreateTRPCReact } from '@trpc/react-query'
import type { AnyRouter } from '@trpc/server'

const REFRESH_TIMESTAMP_KEY = 'markku-last-auth-refresh'
// Must be shorter than server access-token TTL (15 min) and longer than
// realistic inter-tab poll skew.
const REFRESH_SKIP_WINDOW_MS = 10_000

function wasRefreshedRecently(): boolean {
  try {
    const raw = localStorage.getItem(REFRESH_TIMESTAMP_KEY)
    if (!raw) return false
    const ts = Number(raw)
    return Number.isFinite(ts) && Date.now() - ts < REFRESH_SKIP_WINDOW_MS
  } catch {
    return false
  }
}

function markRefreshed(): void {
  try {
    localStorage.setItem(REFRESH_TIMESTAMP_KEY, String(Date.now()))
  } catch {
    // Storage may be unavailable (private mode, quota); fall through.
  }
}

/**
 * Factory function to create tRPC clients with custom auth handling
 * This avoids hardcoding auth store imports in the shared package
 */
export function createTrpcClients<TRouter extends AnyRouter>(options?: {
  appId?: string
  onAuthError?: () => void
}): {
  trpc: CreateTRPCReact<TRouter, unknown>
  trpcClient: ReturnType<typeof createTRPCClient<TRouter>>
  enhancedFetch: (
    input: Parameters<typeof fetch>[0],
    init?: Parameters<typeof fetch>[1],
  ) => Promise<Response>
} {
  const trpc = createTRPCReact<TRouter>()
  let refreshPromise: Promise<boolean> | null = null

  async function enhancedFetch(
    input: Parameters<typeof fetch>[0],
    init?: Parameters<typeof fetch>[1],
  ): Promise<Awaited<ReturnType<typeof fetch>>> {
    const response = await fetch(input, init)

    if (response.status === 401) {
      // Check if this is a token expiration that should trigger refresh
      const shouldRefresh = await checkIfTokenExpired(response)

      if (shouldRefresh) {
        const refreshSuccessful = await handleTokenRefresh()
        if (refreshSuccessful) {
          return fetch(input, init)
        }
      }

      options?.onAuthError?.()
    }

    if (!response.ok) {
      console.error('tRPC fetch error:', response.status, response.statusText)
    }

    return response
  }

  async function checkIfTokenExpired(response: Response): Promise<boolean> {
    try {
      const text = await response.clone().text()
      return text.includes('TOKEN_VALIDATION_FAILED')
    } catch {
      return false
    }
  }

  async function handleTokenRefresh(): Promise<boolean> {
    if (refreshPromise) {
      return refreshPromise
    }

    refreshPromise = performTokenRefresh()

    try {
      const result = await refreshPromise
      return result
    } finally {
      refreshPromise = null
    }
  }

  async function performTokenRefresh(): Promise<boolean> {
    // Serialize /refresh across browser tabs of the same origin. Without this,
    // two tabs can POST /refresh with the same refresh token; one rotation
    // wins and the other triggers the server's reuse-detection path, which
    // revokes the entire session and logs every tab out.
    if (typeof navigator !== 'undefined' && navigator.locks) {
      return navigator.locks.request('markku-auth-refresh', async () => {
        // Another tab may have refreshed while we were queued on the lock —
        // the cookie jar is already fresh, so skip the redundant rotation.
        if (wasRefreshedRecently()) return true
        const ok = await doRefresh()
        if (ok) markRefreshed()
        return ok
      })
    }
    return doRefresh()
  }

  async function doRefresh(): Promise<boolean> {
    try {
      const refreshResponse = await fetch('/api/auth.refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      })

      return refreshResponse.ok
    } catch {
      return false
    }
  }

  const trpcClient = createTRPCClient<TRouter>({
    links: [
      httpBatchLink<TRouter>({
        url: '/api',
        // Send queries as POST so inputs travel in the request body, not the
        // query string. AWS API Gateway HTTP API v2 / CloudFront reject URLs
        // over 8192 bytes with HTTP 413, which we hit when a newsletter
        // audience filter contains hundreds of includeEmails. Requires
        // allowMethodOverride: true on the server adapter.
        methodOverride: 'POST',
        headers: () => ({
          'Content-Type': 'application/json',
          ...(options?.appId && { 'X-App-Id': options.appId }),
        }),
        fetch: enhancedFetch,
      } as any), // eslint-disable-line @typescript-eslint/no-explicit-any
    ],
  })

  return { trpc, trpcClient, enhancedFetch }
}
